import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { SiteHeader } from "../components/SiteHeader";
import { SiteFooter } from "../components/SiteFooter";
import { DigitalCenterPreview } from "../components/DigitalCenterPreview";

// Copy verificado contra CLAUDE.md del cockpit ("Qué es") y contra
// src/connectors/ del backend antes de escribirse — regla de oro 3 del
// proyecto: cero métricas inventadas (nada de % de deflexión, SLA, GB de
// storage), y ninguna funcionalidad que no exista en el código real.
//
// Rediseño 2026-08-17: reestructuración visual sobre el mockup aprobado por
// Jacobo (wasom-landing-review.html) — el copy de las 4 capas y de "¿Qué es
// WASOM?" se preserva sin reescribir (ahora vive como intro de la sección
// "La plataforma"); el copy de "problema" y "cómo funciona" es nuevo, pero
// genérico de industria MiPyME, sin afirmaciones de producto no verificadas.

function Eyebrow({ children }: { children: ReactNode }) {
  return (
    <span
      className="inline-flex items-center gap-2 font-mono text-xs font-semibold uppercase tracking-wide"
      style={{ color: "var(--brand-ink)" }}
    >
      <span className="h-1.5 w-1.5 rounded-full" style={{ background: "var(--brand)" }} />
      {children}
    </span>
  );
}

const PROBLEM_CARDS: Array<{ title: string; body: string; icon: ReactNode }> = [
  {
    title: "Canales desconectados",
    body: "WhatsApp en un celular, Instagram en otra pantalla, el correo en una tercera — nadie ve la conversación completa de un cliente.",
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8}>
        <rect x="3" y="4" width="7" height="7" rx="1.5" />
        <rect x="14" y="4" width="7" height="7" rx="1.5" />
        <rect x="3" y="15" width="7" height="7" rx="1.5" />
        <rect x="14" y="15" width="7" height="7" rx="1.5" />
      </svg>
    ),
  },
  {
    title: "Nadie sabe qué mensaje urge",
    body: "Sin colas ni SLA, un pedido de hoy y un reclamo de hace tres días se ven exactamente igual en la bandeja.",
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8}>
        <circle cx="12" cy="12" r="9" />
        <path d="M12 7v5l3 3" />
      </svg>
    ),
  },
  {
    title: "El software de nivel enterprise no es para vos",
    body: "Las plataformas grandes de contact center están pensadas —y priceadas— para call centers de cientos de agentes, no para tu equipo de tres.",
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8}>
        <path d="M3 21l3-3 12-12 3 3-12 12-3 3z" />
        <path d="M14 5l5 5" />
      </svg>
    ),
  },
];

const LAYERS: Array<{ title: string; body: string; icon: ReactNode }> = [
  {
    title: "Control de canal",
    body:
      "WhatsApp y Messenger operan hoy en un solo lugar; Instagram y TikTok están en desarrollo; email y webchat, más adelante. Multi-instancia por negocio, webhooks entrantes y renderizado nativo saliente — cada canal habla con su propio formato, sin que tu equipo tenga que aprenderlo.",
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8}>
        <path d="M21 11.5a8.38 8.38 0 0 1-8.5 8.5 8.5 8.5 0 0 1-4-1L3 20l1-5.5a8.38 8.38 0 0 1-1-4A8.5 8.5 0 0 1 11.5 2 8.38 8.38 0 0 1 21 11.5z" />
      </svg>
    ),
  },
  {
    title: "Digital Center",
    body:
      "La operación humana: colas con SLA, holding, asignación de chats por capacidad, presencia en vivo, whisper y coaching para supervisores, y un wallboard para ver todo de un vistazo.",
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8}>
        <rect x="3" y="3" width="7" height="18" rx="1.5" />
        <rect x="14" y="3" width="7" height="10" rx="1.5" />
      </svg>
    ),
  },
  {
    title: "Bus BYOB — conecta un motor de agente",
    body:
      "WASOM no te ata a un solo motor de IA: expone un contrato para conectar un motor de agente. El primero es el nuestro; el bus está pensado para sumar otros.",
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8}>
        <path d="M12 2v4M12 18v4M4.9 4.9l2.8 2.8M16.3 16.3l2.8 2.8M2 12h4M18 12h4M4.9 19.1l2.8-2.8M16.3 7.7l2.8-2.8" />
        <circle cx="12" cy="12" r="3" />
      </svg>
    ),
  },
  {
    title: "Conectores de salida",
    body:
      "En desarrollo: conectores hacia CRM, mesa de ayuda y pagos, y una API pública para que integradores externos conecten WASOM con lo que tu negocio ya usa.",
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8}>
        <rect x="4" y="4" width="16" height="16" rx="3" />
        <path d="M9 9h6v6H9z" />
      </svg>
    ),
  },
];

const HOW_STEPS: Array<{ num: string; title: string; body: string }> = [
  {
    num: "01",
    title: "Conectá tus canales",
    body: "WhatsApp Business y Messenger hoy (Instagram y TikTok, en desarrollo; email y webchat, más adelante). Nosotros conectamos tus cuentas con vos; te decimos antes si tu número de WhatsApp necesita algún paso con Meta.",
  },
  {
    num: "02",
    title: "Configurá tus colas",
    body: "Definís equipos, SLAs y a quién le llega cada tipo de conversación — o dejás una cola simple por ahora.",
  },
  {
    num: "03",
    title: "Tu equipo atiende",
    body: "Desde una sola pantalla, con todo el historial del cliente a mano — conectamos un motor de agente a tu cuenta cuando estés listo.",
  },
];

export function LandingPage() {
  return (
    <div className="min-h-screen" style={{ background: "var(--bg)", color: "var(--text-1)" }}>
      <SiteHeader />

      <main>
        {/* Hero */}
        <section
          className="overflow-hidden px-6 py-14 sm:py-20 lg:py-24"
          style={{ background: "radial-gradient(760px 420px at 14% -8%, var(--brand-subtle), transparent 60%), var(--bg)" }}
        >
          <div className="mx-auto grid max-w-6xl items-center gap-12 lg:grid-cols-2 lg:gap-16">
            <div>
              <Eyebrow>Intelligent Contact</Eyebrow>
              <h1 className="font-display mt-4 text-4xl font-bold leading-[1.05] sm:text-5xl lg:text-[60px]">
                Tu WhatsApp y tu Messenger,{" "}
                <span style={{ color: "var(--brand-ink)" }}>atendidos como un solo negocio.</span>
              </h1>
              <p className="text-md mt-5 max-w-[46ch]" style={{ color: "var(--text-2)" }}>
                WASOM es la plataforma de contact center para la MiPyME latinoamericana — colas, SLAs, tu
                equipo y un asistente de IA en un solo lugar, con una complejidad de
                implementación pensada para equipos chicos.
              </p>
              <div className="mt-8 flex flex-wrap items-center gap-3.5">
                <Link to="/contacto">
                  <button
                    className="rounded-[var(--radius-lg)] px-7 py-3 text-sm font-semibold"
                    style={{ background: "var(--brand)", color: "var(--brand-on)" }}
                  >
                    Hablemos de tu operación
                  </button>
                </Link>
                <a
                  href="#como-funciona"
                  className="rounded-[var(--radius-lg)] border px-7 py-3 text-sm font-semibold"
                  style={{ borderColor: "var(--border)" }}
                >
                  Ver cómo funciona ↓
                </a>
              </div>
              <div className="mt-7 flex items-start gap-2.5 text-xs" style={{ color: "var(--text-3)" }}>
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={2}
                  className="mt-0.5 flex-shrink-0"
                >
                  <path d="M20 6L9 17l-5-5" />
                </svg>
                <span>Te contamos el modelo comercial en la primera conversación, sin compromiso.</span>
              </div>
            </div>

            <DigitalCenterPreview />
          </div>
        </section>

        {/* El problema real */}
        <section
          id="producto"
          className="border-y px-6 py-16 sm:py-20"
          style={{ borderColor: "var(--border)", background: "var(--surface)" }}
        >
          <div className="mx-auto mb-12 max-w-xl text-center">
            <Eyebrow>El problema real</Eyebrow>
            <h2 className="font-display mt-3 text-3xl font-bold sm:text-4xl">
              Cada canal nuevo es una pestaña más, no una operación mejor
            </h2>
          </div>
          <div className="mx-auto grid max-w-5xl gap-6 sm:grid-cols-3">
            {PROBLEM_CARDS.map((card) => (
              <div
                key={card.title}
                className="rounded-[var(--radius-xl)] border p-7"
                style={{ borderColor: "var(--border)", background: "var(--bg)" }}
              >
                <div
                  className="mb-[18px] flex h-10 w-10 items-center justify-center rounded-[var(--radius-md)]"
                  style={{ background: "var(--surface-2)", color: "var(--text-1)" }}
                >
                  {card.icon}
                </div>
                <h3 className="text-base font-bold">{card.title}</h3>
                <p className="mt-2 text-sm leading-relaxed" style={{ color: "var(--text-2)" }}>
                  {card.body}
                </p>
              </div>
            ))}
          </div>
        </section>

        {/* La plataforma — las 4 capas (copy verificado, sin reescribir) */}
        <section id="capas" className="px-6 py-16 sm:py-20">
          <div className="mx-auto mb-12 max-w-2xl">
            <Eyebrow>La plataforma</Eyebrow>
            <h2 className="font-display mt-3 text-3xl font-bold sm:text-4xl">
              Un solo contact center, no cuatro herramientas pegadas con cinta
            </h2>
            <p className="text-md mt-4" style={{ color: "var(--text-2)" }}>
              WASOM es una plataforma <strong>CCaaS</strong> (Contact Center as a Service)
              multi-instancia. Toma el control de los canales conversacionales de tu negocio,
              gestiona la atención con colas y SLAs y, a medida que salgan los conectores, se integra con
              los sistemas que ya usás — sin que tengas que construir ni mantener nada de eso vos mismo.
            </p>
          </div>
          <div className="mx-auto grid max-w-5xl gap-5 sm:grid-cols-2">
            {LAYERS.map((layer) => (
              <div
                key={layer.title}
                className="flex gap-[18px] rounded-[var(--radius-xl)] border p-7"
                style={{ borderColor: "var(--border)", background: "var(--surface)" }}
              >
                <div
                  className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-[var(--radius-md)]"
                  style={{ background: "var(--brand-subtle)", color: "var(--brand-ink)" }}
                >
                  {layer.icon}
                </div>
                <div>
                  <h3 className="text-base font-bold">{layer.title}</h3>
                  <p className="mt-1.5 text-sm leading-relaxed" style={{ color: "var(--text-2)" }}>
                    {layer.body}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Puesta en marcha — 3 pasos, secuenciales de verdad */}
        <section
          id="como-funciona"
          className="border-y px-6 py-16 sm:py-20"
          style={{ borderColor: "var(--border)", background: "var(--surface)" }}
        >
          <div className="mx-auto mb-14 max-w-xl text-center">
            <Eyebrow>Puesta en marcha</Eyebrow>
            <h2 className="font-display mt-3 text-3xl font-bold sm:text-4xl">Tres pasos, no un proyecto de meses</h2>
          </div>
          <div className="relative mx-auto grid max-w-4xl gap-10 sm:grid-cols-3">
            <div
              className="pointer-events-none absolute top-[23px] hidden h-px sm:block"
              style={{ left: "12%", right: "12%", background: "var(--border)" }}
              aria-hidden
            />
            {HOW_STEPS.map((step) => (
              <div key={step.num} className="relative z-10 px-2 text-center">
                <div
                  className="mx-auto mb-5 flex h-11 w-11 items-center justify-center rounded-full border font-mono text-sm font-bold"
                  style={{ borderColor: "var(--border)", background: "var(--bg)", color: "var(--text-2)" }}
                >
                  {step.num}
                </div>
                <h3 className="text-[15.5px] font-bold">{step.title}</h3>
                <p className="mx-auto mt-2 max-w-[30ch] text-sm leading-relaxed" style={{ color: "var(--text-2)" }}>
                  {step.body}
                </p>
              </div>
            ))}
          </div>
        </section>

        {/* CTA final */}
        <section className="px-6 py-16 sm:py-20">
          <div
            className="mx-auto flex max-w-3xl flex-col items-center gap-4 rounded-[var(--radius-2xl)] border px-8 py-14 text-center"
            style={{ background: "var(--brand-subtle)", borderColor: "var(--brand-subtle)" }}
          >
            <h2 className="font-display text-3xl font-bold sm:text-4xl" style={{ color: "var(--brand-ink)" }}>
              Contanos cómo atendés hoy
            </h2>
            <p className="max-w-[46ch] text-sm" style={{ color: "var(--text-2)" }}>
              Te mostramos, con tus propios canales, cómo se vería tu operación en WASOM — sin compromiso.
            </p>
            <Link to="/contacto">
              <button
                className="mt-2 rounded-[var(--radius-lg)] px-8 py-3 text-sm font-semibold"
                style={{ background: "var(--brand)", color: "var(--brand-on)" }}
              >
                Escribinos
              </button>
            </Link>
          </div>
        </section>
      </main>

      <SiteFooter />
    </div>
  );
}
